import { useEffect, useState, useRef } from 'react';
import { Plus, Pencil, Trash2, Search, Upload, Download, Loader2, CalendarCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { adminAPI, mainCategoriesAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { toSASTDateInput } from '@/lib/tz';

const EMPTY_FORM = {
  name: '', description: '', contact_email: '', contact_phone: '',
  contact_person_name: '', contact_person_email: '', contact_person_phone: '',
  image_url: '', is_active: true, window_open_date: '', window_close_date: '',
  main_category_id: '',
  delivery_charge_enabled: false,
  delivery_charge_type: 'fixed', // 'fixed' or 'percentage'
  delivery_charge_value: 0,
  admin_fee_enabled: false,
  admin_fee_percentage: 0,
  moq_value_zar: 0,
  moq_item_count: 0,
  payment_workflow: 'on_checkout',
  payment_due_hours_override: '',
  is_vat_vendor: false,
};

const SORT_OPTIONS = [
  { key: 'name', label: 'Name (A–Z)' },
  { key: 'close_soonest', label: 'Window closes soonest' },
];
const SORT_STORAGE_KEY = 'avz.adminSuppliers.sortBy';

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [sortBy, setSortBy] = useState(() => {
    if (typeof window === 'undefined') return 'name';
    const saved = window.localStorage.getItem(SORT_STORAGE_KEY);
    return SORT_OPTIONS.some(o => o.key === saved) ? saved : 'name';
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef(null);
  // Notes (loaded with editing supplier; local-first edits then synced to API)
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  const fetchSuppliers = () => {
    setLoading(true);
    adminAPI.listSuppliers()
      .then(r => setSuppliers(r.data))
      .catch(() => toast.error('Failed to load suppliers'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let active = true;
    adminAPI.listSuppliers()
      .then(r => { if (active) setSuppliers(r.data); })
      .catch(() => toast.error('Failed to load suppliers'))
      .finally(() => { if (active) setLoading(false); });
    mainCategoriesAPI.list().then(r => { if (active) setMainCategories(r.data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Active suppliers first, then inactive; sort within each group according to
  // the admin's saved preference (default = alphabetical, case-insensitive).
  // - 'close_soonest': nearest upcoming window close ranks first. Suppliers
  //   without a close date sink to the bottom; ties fall back to alpha.
  const sortedSuppliers = [...suppliers].sort((a, b) => {
    if (sortBy === 'close_soonest') {
      const aClose = a.window_close_date || '';
      const bClose = b.window_close_date || '';
      if (aClose && bClose && aClose !== bClose) return aClose < bClose ? -1 : 1;
      if (aClose && !bClose) return -1;
      if (!aClose && bClose) return 1;
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  const persistSort = (key) => {
    setSortBy(key);
    try { window.localStorage.setItem(SORT_STORAGE_KEY, key); } catch { /* ignore */ }
  };

  // Returns 'open' | 'upcoming' | 'closed' | null (null when dates missing)
  const windowState = (sup) => {
    const open = sup.window_open_date || '';
    const close = sup.window_close_date || '';
    if (!open || !close) return null;
    const now = new Date().toISOString();
    if (now < open) return 'upcoming';
    if (now > close) return 'closed';
    return 'open';
  };

  // The order window OVERRIDES the manual Active/Inactive flag:
  //  - with a window set → "active" only when it's currently open (is_active ignored)
  //  - without a window → fall back to the manual is_active flag
  const isDerivedActive = (sup) => {
    const ws = windowState(sup);
    if (ws !== null) return ws === 'open';
    return sup.is_active !== false;
  };

  const matchesSearch = (s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.contact_email || '').toLowerCase().includes(search.toLowerCase());

  const allFiltered = sortedSuppliers.filter(matchesSearch);
  const activeSuppliers = allFiltered.filter(isDerivedActive);
  const inactiveSuppliers = allFiltered.filter(s => !isDerivedActive(s));

  // Apply tab filter
  const visibleActive = activeFilter === 'inactive' ? [] : activeSuppliers;
  const visibleInactive = activeFilter === 'active' ? [] : inactiveSuppliers;
  const totalVisible = visibleActive.length + visibleInactive.length;

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      // Rendered in SAST so the displayed date matches the picked calendar date.
      return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', timeZone: 'Africa/Johannesburg' });
    } catch { return iso.slice(0, 10); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setNotes([]);
    setNewNoteText('');
    setDialogOpen(true);
  };

  const openEdit = (sup) => {
    setEditing(sup);
    setForm({
      name: sup.name, description: sup.description || '', contact_email: sup.contact_email || '',
      contact_phone: sup.contact_phone || '',
      contact_person_name: sup.contact_person_name || '',
      contact_person_email: sup.contact_person_email || '',
      contact_person_phone: sup.contact_person_phone || '',
      image_url: sup.image_url || '', is_active: sup.is_active !== false,
      window_open_date: toSASTDateInput(sup.window_open_date),
      window_close_date: toSASTDateInput(sup.window_close_date),
      main_category_id: sup.main_category_id || '',
      delivery_charge_enabled: !!sup.delivery_charge_enabled,
      delivery_charge_type: sup.delivery_charge_type || 'fixed',
      delivery_charge_value: Number(sup.delivery_charge_value || 0),
      admin_fee_enabled: !!sup.admin_fee_enabled,
      admin_fee_percentage: Number(sup.admin_fee_percentage || 0),
      moq_value_zar: Number(sup.moq_value_zar || 0),
      moq_item_count: Number(sup.moq_item_count || 0),
      payment_workflow: sup.payment_workflow || 'on_checkout',
      payment_due_hours_override: sup.payment_due_hours_override != null ? String(sup.payment_due_hours_override) : '',
      is_vat_vendor: !!sup.is_vat_vendor,
    });
    setNotes(sup.notes || []);
    setNewNoteText('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.window_open_date) payload.window_open_date = `${payload.window_open_date}T00:00:00Z`;
      if (payload.window_close_date) payload.window_close_date = `${payload.window_close_date}T23:59:59Z`;
      payload.delivery_charge_value = Number(payload.delivery_charge_value || 0);
      payload.admin_fee_percentage = Number(payload.admin_fee_percentage || 0);
      payload.payment_due_hours_override =
        payload.payment_due_hours_override === '' || payload.payment_due_hours_override == null
          ? null
          : Number(payload.payment_due_hours_override);
      if (editing) {
        await adminAPI.updateSupplier(editing.id, payload);
        toast.success('Supplier updated');
      } else {
        await adminAPI.createSupplier(payload);
        toast.success('Supplier created');
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try { await adminAPI.deleteSupplier(id); toast.success('Deleted'); fetchSuppliers(); }
    catch { toast.error('Failed to delete'); }
  };

  // ---- Supplier Notes -----------------------------------------------------
  const handleAddNote = async () => {
    const text = newNoteText.trim();
    if (!text || !editing) return;
    setSavingNote(true);
    try {
      const res = await adminAPI.addSupplierNote(editing.id, text);
      setNotes(prev => [...prev, res.data]);
      setNewNoteText('');
      toast.success('Note added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text);
  };

  const handleSaveEditNote = async () => {
    if (!editing || !editingNoteId) return;
    const text = editingNoteText.trim();
    if (!text) return;
    try {
      const res = await adminAPI.updateSupplierNote(editing.id, editingNoteId, text);
      setNotes(prev => prev.map(n => (n.id === editingNoteId ? res.data : n)));
      setEditingNoteId(null);
      setEditingNoteText('');
      toast.success('Note updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!editing) return;
    if (!window.confirm('Delete this note?')) return;
    try {
      await adminAPI.deleteSupplierNote(editing.id, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await adminAPI.importSuppliers(formData);
      toast.success(`Created: ${res.data.created}, Updated: ${res.data.updated}`);
      fetchSuppliers();
    } catch { toast.error('Import failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await adminAPI.downloadSupplierTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'AVZ_Supplier_Template.xlsx'; a.click();
    } catch { toast.error('Failed to download template'); }
  };

  const [exporting, setExporting] = useState(false);
  const handleExportSuppliers = async () => {
    setExporting(true);
    try {
      const res = await adminAPI.exportSuppliers();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'AVZ_Suppliers_Export.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5 MB');
      e.target.value = '';
      return;
    }
    setLogoUploading(true);
    try {
      const res = await adminAPI.uploadImage(file);
      setForm(f => ({ ...f, image_url: res.data.url }));
      toast.success('Logo uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const renderSupplierRow = (sup) => {
    const ws = windowState(sup);
    const mc = mainCategories.find(m => m.id === sup.main_category_id);
    return (
      <div
        key={sup.id}
        data-testid={`supplier-row-${sup.id}`}
        className="flex items-center gap-4 p-4 sm:p-5 rounded-2xl bg-card border border-border hover:border-teal/30 transition-colors cursor-pointer group"
        onClick={() => openEdit(sup)}
      >
        {sup.image_url ? (
          <img
            src={sup.image_url}
            alt={sup.name}
            className="w-12 h-12 rounded-xl object-contain bg-white/5 flex-shrink-0"
            onError={(e) => {
              // Hide the broken <img> and reveal the initial-letter fallback sibling.
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.nextElementSibling;
              if (fb) fb.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`w-12 h-12 rounded-xl bg-teal/10 items-center justify-center flex-shrink-0 ${sup.image_url ? 'hidden' : 'flex'}`}
        >
          <span className="font-heading text-lg text-teal">{sup.name[0]}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-body text-base font-semibold text-foreground group-hover:text-teal transition-colors">{sup.name}</h3>
            {mc && (
              <span
                data-testid={`supplier-main-${sup.id}`}
                className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-vintage/15 text-vintage uppercase tracking-wider"
              >
                {mc.name}
              </span>
            )}
            {sup.is_active === false && (
              <span className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-400/15 text-red-400">
                Disabled
              </span>
            )}
            {ws === 'open' && (
              <span
                data-testid={`window-open-${sup.id}`}
                className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal/15 text-teal inline-flex items-center gap-1 animate-pulse"
              >
                <CalendarCheck className="h-3 w-3" strokeWidth={2.5} />
                Window Open
              </span>
            )}
            {ws === 'upcoming' && (
              <span
                data-testid={`window-upcoming-${sup.id}`}
                className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-gold/15 text-gold"
              >
                Window Upcoming
              </span>
            )}
            {ws === 'closed' && (
              <span
                data-testid={`window-closed-${sup.id}`}
                className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground/40"
              >
                Window Closed
              </span>
            )}
            {ws === null && (
              <span
                data-testid={`window-none-${sup.id}`}
                className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground/40"
              >
                No Window Set
              </span>
            )}
          </div>
          <p className="font-body text-xs text-foreground/50 mt-0.5 truncate">{sup.contact_email || sup.description || '-'}</p>
          {(sup.window_open_date || sup.window_close_date) && (
            <p
              data-testid={`window-dates-${sup.id}`}
              className="font-body text-[11px] text-foreground/40 mt-1 inline-flex items-center gap-1"
            >
              <CalendarCheck className="h-3 w-3" strokeWidth={2} />
              {fmtDate(sup.window_open_date) || '?'} — {fmtDate(sup.window_close_date) || '?'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={e => { e.stopPropagation(); openEdit(sup); }}>
            <Pencil className="h-3.5 w-3.5 text-foreground/50" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-red-900/20" onClick={e => { e.stopPropagation(); handleDelete(sup.id); }}>
            <Trash2 className="h-3.5 w-3.5 text-red-400/60" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="admin-suppliers">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Suppliers</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">{suppliers.length} suppliers</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={handleDownloadTemplate} variant="outline" className="rounded-full border-border font-body text-xs gap-1.5" size="sm" data-testid="download-template-btn">
            <Download className="h-3.5 w-3.5" /> Template
          </Button>
          <Button onClick={handleExportSuppliers} disabled={exporting} variant="outline" className="rounded-full border-gold/40 text-gold font-body text-xs gap-1.5" size="sm" data-testid="export-suppliers-btn">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" data-testid="upload-suppliers-input" />
            <Button as="span" variant="outline" className="rounded-full border-teal/30 text-teal font-body text-xs gap-1.5 pointer-events-none" size="sm" disabled={uploading}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import
            </Button>
          </label>
          <Button onClick={openCreate} data-testid="add-supplier-btn" className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2">
            <Plus className="h-4 w-4" /> Add Supplier
          </Button>
        </div>
      </div>

      {/* Search + Filter tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
          <Input
            data-testid="supplier-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search suppliers..."
            className="pl-10 rounded-xl bg-card border-border font-body text-sm"
          />
        </div>
        <div className="inline-flex rounded-full bg-card border border-border p-1" data-testid="supplier-filter-tabs">
          {[
            { key: 'all', label: 'All', count: activeSuppliers.length + inactiveSuppliers.length },
            { key: 'active', label: 'Active', count: activeSuppliers.length },
            { key: 'inactive', label: 'Inactive', count: inactiveSuppliers.length },
          ].map(t => (
            <button
              key={t.key}
              data-testid={`filter-${t.key}`}
              onClick={() => setActiveFilter(t.key)}
              className={`px-3 sm:px-4 py-1.5 rounded-full font-body text-xs font-semibold uppercase tracking-wider transition-colors ${
                activeFilter === t.key
                  ? 'bg-teal text-white shadow-sm'
                  : 'text-foreground/60 hover:text-foreground'
              }`}
            >
              {t.label}
              <span className={`ml-1.5 font-body text-[10px] ${activeFilter === t.key ? 'text-white/70' : 'text-foreground/40'}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-2" data-testid="supplier-sort-control">
          <label htmlFor="supplier-sort" className="font-body text-[11px] uppercase tracking-wider text-foreground/50 hidden sm:inline">
            Sort
          </label>
          <select
            id="supplier-sort"
            data-testid="supplier-sort-select"
            value={sortBy}
            onChange={(e) => persistSort(e.target.value)}
            className="rounded-full bg-card border border-border px-3 py-1.5 font-body text-xs font-semibold text-foreground/80 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-teal/30"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 rounded-2xl bg-muted animate-pulse" />)}</div>
      ) : totalVisible === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-muted/30 border border-border/50">
          <p className="font-heading text-xl text-foreground/50">No suppliers found</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="suppliers-list">
          {visibleActive.length > 0 && (
            <section data-testid="suppliers-section-active">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                <h2 className="font-body text-xs font-bold uppercase tracking-[0.18em] text-foreground">
                  Active Suppliers
                </h2>
                <span className="font-body text-xs text-foreground/40">({visibleActive.length})</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2">
                {visibleActive.map(sup => renderSupplierRow(sup))}
              </div>
            </section>
          )}
          {visibleInactive.length > 0 && (
            <section data-testid="suppliers-section-inactive">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-2 w-2 rounded-full bg-foreground/30" />
                <h2 className="font-body text-xs font-bold uppercase tracking-[0.18em] text-foreground/60">
                  Inactive Suppliers
                </h2>
                <span className="font-body text-xs text-foreground/40">({visibleInactive.length})</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2">
                {visibleInactive.map(sup => renderSupplierRow(sup))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background rounded-2xl max-w-2xl lg:max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {editing ? 'Edit Supplier' : 'Add Supplier'}
            </DialogTitle>
            <DialogDescription className="font-body text-xs text-foreground/60">
              {editing
                ? 'Update supplier details, contact, MOQ rules and internal notes.'
                : 'Create a new supplier. Notes can be added once the supplier exists.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Name *</Label>
              <Input data-testid="supplier-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Email</Label>
                <Input value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Phone</Label>
                <Input value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Supplier Logo</Label>
              <div className="flex items-start gap-3 mt-1.5">
                {form.image_url ? (
                  <div className="relative flex-shrink-0">
                    <img
                      src={form.image_url}
                      alt="Supplier logo"
                      className="w-20 h-20 rounded-xl object-contain bg-white/5 border border-border"
                      data-testid="supplier-logo-preview"
                    />
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                      data-testid="supplier-logo-clear"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-700 text-white hover:bg-red-800 flex items-center justify-center shadow-md"
                      aria-label="Remove logo"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-muted/40 border border-dashed border-border flex items-center justify-center flex-shrink-0">
                    <Upload className="h-6 w-6 text-foreground/30" strokeWidth={1.5} />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    ref={logoFileRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="supplier-logo-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoFileRef.current?.click()}
                    disabled={logoUploading}
                    data-testid="supplier-logo-upload-btn"
                    className="rounded-full border-border font-body text-xs gap-1.5 w-full justify-center"
                    size="sm"
                  >
                    {logoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {form.image_url ? 'Replace logo' : 'Upload logo'}
                  </Button>
                  <Input
                    value={form.image_url}
                    onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                    className="rounded-xl bg-muted border-border font-body text-xs"
                    placeholder="or paste a URL"
                    data-testid="supplier-logo-url"
                  />
                  <p className="font-body text-[10px] text-foreground/40">PNG, JPG or SVG. Max 5 MB.</p>
                </div>
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Main Category</Label>
              <select
                data-testid="supplier-main-select"
                value={form.main_category_id}
                onChange={e => setForm(f => ({...f, main_category_id: e.target.value}))}
                className="mt-1.5 w-full rounded-xl bg-muted border border-border px-3 py-2 font-body text-sm text-foreground"
              >
                <option value="">— None —</option>
                {[...mainCategories].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(mc => (
                  <option key={mc.id} value={mc.id}>{mc.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Window Open</Label>
                <Input type="date" value={form.window_open_date} onChange={e => setForm(f => ({...f, window_open_date: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground/70 font-medium">Window Close</Label>
                <Input type="date" value={form.window_close_date} onChange={e => setForm(f => ({...f, window_close_date: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium">Payment Workflow</Label>
              <select
                data-testid="supplier-payment-workflow-select"
                value={form.payment_workflow}
                onChange={e => setForm(f => ({...f, payment_workflow: e.target.value}))}
                className="mt-1.5 w-full rounded-xl bg-muted border border-border px-3 py-2 font-body text-sm text-foreground"
              >
                <option value="on_checkout">Pay on checkout (default)</option>
                <option value="after_confirmation">Pay after confirmation</option>
              </select>
              <p className="font-body text-[11px] text-foreground/50 mt-1">
                &quot;Pay after confirmation&quot; lets customers place orders without paying — you confirm first, then they&apos;re sent a payment link by email &amp; WhatsApp.
              </p>
              {form.payment_workflow === 'after_confirmation' && (
                <div className="mt-3">
                  <Label className="font-body text-xs text-foreground/70 font-medium">Pay-by window override (hours)</Label>
                  <Input
                    data-testid="supplier-payment-due-override"
                    type="number"
                    min={1}
                    max={720}
                    value={form.payment_due_hours_override}
                    onChange={e => setForm(f => ({ ...f, payment_due_hours_override: e.target.value }))}
                    placeholder="Leave blank to use the global default"
                    className="rounded-xl bg-muted border-border mt-1.5 w-full sm:w-64"
                  />
                  <p className="font-body text-[11px] text-foreground/50 mt-1">
                    How long customers have to pay after you request payment for this supplier. Blank = use the global &quot;Pay-by window&quot; in Settings.
                  </p>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-border bg-muted/40 p-3 sm:p-4 space-y-3" data-testid="supplier-delivery-fieldset">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="font-body text-sm text-foreground font-medium">Delivery / Handling Charge</Label>
                  <p className="font-body text-[11px] text-foreground/50 mt-0.5">
                    Applied to every order placed with this supplier.
                  </p>
                </div>
                <Switch
                  data-testid="supplier-delivery-enabled"
                  checked={form.delivery_charge_enabled}
                  onCheckedChange={v => setForm(f => ({ ...f, delivery_charge_enabled: v }))}
                />
              </div>
              {form.delivery_charge_enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-body text-xs text-foreground/60 font-medium">Type</Label>
                    <select
                      data-testid="supplier-delivery-type"
                      value={form.delivery_charge_type}
                      onChange={e => setForm(f => ({ ...f, delivery_charge_type: e.target.value }))}
                      className="mt-1.5 w-full rounded-xl bg-card border border-border px-3 py-2 font-body text-sm text-foreground"
                    >
                      <option value="fixed">Fixed amount (R)</option>
                      <option value="percentage">Percentage of subtotal (%)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="font-body text-xs text-foreground/60 font-medium">
                      {form.delivery_charge_type === 'percentage' ? 'Percent (%)' : 'Amount (R)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      data-testid="supplier-delivery-value"
                      value={form.delivery_charge_value}
                      onChange={e => setForm(f => ({ ...f, delivery_charge_value: e.target.value }))}
                      className="rounded-xl bg-card border-border mt-1.5"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-border bg-muted/40 p-3 sm:p-4 space-y-3" data-testid="supplier-admin-fee-fieldset">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="font-body text-sm text-foreground font-medium">Admin Fee (markup override)</Label>
                  <p className="font-body text-[11px] text-foreground/50 mt-0.5">
                    When enabled, this % supersedes the global markup for this supplier's products (baked into the price shown to customers).
                  </p>
                </div>
                <Switch
                  data-testid="supplier-admin-fee-enabled"
                  checked={form.admin_fee_enabled}
                  onCheckedChange={v => setForm(f => ({ ...f, admin_fee_enabled: v }))}
                />
              </div>
              {form.admin_fee_enabled && (
                <div>
                  <Label className="font-body text-xs text-foreground/60 font-medium">Admin Fee (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    data-testid="supplier-admin-fee-value"
                    value={form.admin_fee_percentage}
                    onChange={e => setForm(f => ({ ...f, admin_fee_percentage: e.target.value }))}
                    className="rounded-xl bg-card border-border mt-1.5"
                  />
                </div>
              )}
            </div>
            <div className="rounded-xl border border-border bg-muted/40 p-3 sm:p-4 space-y-3" data-testid="supplier-vat-fieldset">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="font-body text-sm text-foreground font-medium">VAT Vendor</Label>
                  <p className="font-body text-[11px] text-foreground/50 mt-0.5">
                    When enabled, this supplier's cost prices are treated as VAT-inclusive and a VAT-exclusive cost is auto-computed for each of their products. Existing products with a manual VAT override are left untouched.
                  </p>
                </div>
                <Switch
                  data-testid="supplier-vat-vendor-enabled"
                  checked={form.is_vat_vendor}
                  onCheckedChange={v => setForm(f => ({ ...f, is_vat_vendor: v }))}
                />
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/40 p-3 sm:p-4 space-y-3" data-testid="supplier-contact-person-fieldset">
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Primary Contact Person</Label>
                <p className="font-body text-[11px] text-foreground/50 mt-0.5">
                  Admin-only. The person we email when sending POs or following up on stock.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="font-body text-xs text-foreground/60 font-medium">Name</Label>
                  <Input
                    data-testid="supplier-contact-person-name"
                    value={form.contact_person_name}
                    onChange={e => setForm(f => ({ ...f, contact_person_name: e.target.value }))}
                    className="rounded-xl bg-card border-border mt-1.5"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <Label className="font-body text-xs text-foreground/60 font-medium">Email</Label>
                  <Input
                    data-testid="supplier-contact-person-email"
                    type="email"
                    value={form.contact_person_email}
                    onChange={e => setForm(f => ({ ...f, contact_person_email: e.target.value }))}
                    className="rounded-xl bg-card border-border mt-1.5"
                    placeholder="jane@supplier.com"
                  />
                </div>
                <div>
                  <Label className="font-body text-xs text-foreground/60 font-medium">Phone</Label>
                  <Input
                    data-testid="supplier-contact-person-phone"
                    value={form.contact_person_phone}
                    onChange={e => setForm(f => ({ ...f, contact_person_phone: e.target.value }))}
                    className="rounded-xl bg-card border-border mt-1.5"
                    placeholder="+27 82 123 4567"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-muted/40 p-3 sm:p-4 space-y-3" data-testid="supplier-moq-fieldset">
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Minimum Order Quantity (MOQ)</Label>
                <p className="font-body text-[11px] text-foreground/50 mt-0.5">
                  Admin-only. Used for PO planning. Leave 0 to disable a specific check.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-body text-xs text-foreground/60 font-medium">Minimum value (R)</Label>
                  <Input
                    data-testid="supplier-moq-value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.moq_value_zar}
                    onChange={e => setForm(f => ({ ...f, moq_value_zar: e.target.value }))}
                    className="rounded-xl bg-card border-border mt-1.5"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label className="font-body text-xs text-foreground/60 font-medium">Minimum item count</Label>
                  <Input
                    data-testid="supplier-moq-count"
                    type="number"
                    min="0"
                    step="1"
                    value={form.moq_item_count}
                    onChange={e => setForm(f => ({ ...f, moq_item_count: e.target.value }))}
                    className="rounded-xl bg-card border-border mt-1.5"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {editing && (
              <div className="rounded-xl border border-border bg-muted/40 p-3 sm:p-4 space-y-3" data-testid="supplier-notes-fieldset">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-body text-sm text-foreground font-medium">Notes</Label>
                    <p className="font-body text-[11px] text-foreground/50 mt-0.5">
                      Internal supplier notes — dated, editable, deletable.
                    </p>
                  </div>
                  <span className="font-body text-[11px] text-foreground/50" data-testid="supplier-notes-count">
                    {notes.length} note{notes.length === 1 ? '' : 's'}
                  </span>
                </div>

                <div className="flex gap-2">
                  <textarea
                    data-testid="supplier-note-input"
                    value={newNoteText}
                    onChange={e => setNewNoteText(e.target.value)}
                    rows={2}
                    placeholder="Add a note (Ctrl+Enter to save)…"
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAddNote();
                    }}
                    className="flex-1 rounded-xl bg-card border border-border px-3 py-2 font-body text-sm text-foreground resize-none focus:border-teal focus:ring-1 focus:ring-teal/30"
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={savingNote || !newNoteText.trim()}
                    data-testid="supplier-note-add-btn"
                    size="sm"
                    className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-xs px-3 self-start"
                  >
                    {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    Add
                  </Button>
                </div>

                {notes.length === 0 ? (
                  <p className="font-body text-xs text-foreground/40 italic text-center py-3">
                    No notes yet.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {[...notes].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map((note) => {
                      const created = note.created_at ? new Date(note.created_at) : null;
                      const updated = note.updated_at ? new Date(note.updated_at) : null;
                      const wasEdited = created && updated && created.getTime() !== updated.getTime();
                      const isEditingThis = editingNoteId === note.id;
                      return (
                        <div
                          key={note.id}
                          data-testid={`supplier-note-${note.id}`}
                          className="rounded-lg bg-card border border-border/60 p-3"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <p className="font-body text-[10px] uppercase tracking-[0.15em] text-foreground/50">
                              {created ? created.toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Johannesburg' }) : ''}
                              {wasEdited && <span className="ml-2 text-foreground/40 normal-case tracking-normal">(edited)</span>}
                            </p>
                            <div className="flex items-center gap-1">
                              {!isEditingThis && (
                                <button
                                  type="button"
                                  onClick={() => handleStartEditNote(note)}
                                  data-testid={`supplier-note-edit-btn-${note.id}`}
                                  className="text-foreground/50 hover:text-foreground p-1 rounded-full hover:bg-muted"
                                  aria-label="Edit note"
                                >
                                  <Pencil className="h-3 w-3" strokeWidth={2} />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteNote(note.id)}
                                data-testid={`supplier-note-delete-btn-${note.id}`}
                                className="text-foreground/50 hover:text-red-400 p-1 rounded-full hover:bg-red-900/20"
                                aria-label="Delete note"
                              >
                                <Trash2 className="h-3 w-3" strokeWidth={2} />
                              </button>
                            </div>
                          </div>
                          {isEditingThis ? (
                            <div className="space-y-2">
                              <textarea
                                data-testid={`supplier-note-edit-input-${note.id}`}
                                value={editingNoteText}
                                onChange={e => setEditingNoteText(e.target.value)}
                                rows={2}
                                className="w-full rounded-lg bg-muted border border-border px-3 py-2 font-body text-sm text-foreground resize-none focus:border-teal focus:ring-1 focus:ring-teal/30"
                              />
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                                  className="rounded-full text-xs px-3"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handleSaveEditNote}
                                  disabled={!editingNoteText.trim()}
                                  data-testid={`supplier-note-save-edit-${note.id}`}
                                  className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light text-xs px-3"
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="font-body text-sm text-foreground/85 whitespace-pre-wrap leading-relaxed">
                              {note.text}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({...f, is_active: v}))} />
              <span className="font-body text-sm text-foreground/60">{form.is_active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full border-border font-body text-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="supplier-save-btn" className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
